import { Circ, Expo, Linear, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { PlayMode } from "html-living-framework";
import { shuffle } from "html-living-framework/lib/Utils/ArrayUtils";
import { isMobile } from "html-living-framework/lib/Utils/PlatformUtils";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import { CaptionToken } from "../data/CaptionToken";
import TrashGameModel from "../data/TrashGameModel";
import {TrashType} from "../data/TrashType";
import { randomExhaust } from "../data/VO";
import * as VO from "../data/VO";
import { Button, OutlinedButton } from "../gameobjects/Button";
import {Oscar} from "../gameobjects/Oscar";
import { OscarCollect } from "../gameobjects/OscarCollect";
import {TrashBin} from "../gameobjects/TrashBin";
import { TrashBlastEasterEgg } from "../gameobjects/TrashBlastEasterEgg";
import {TrashItem} from "../gameobjects/TrashItem";
import { TrashItemCollect } from "../gameobjects/TrashItemCollect";
import {TrashCollectLayout_hud, TrashCollectLayout_main} from "../layout/TrashCollectLayout";
import {AbstractGameState} from "./AbstractGameState";
import { TrashCollectOutroScreen } from "./TrashCollectOutroScreen";
import { TrashReturnScreen } from "./TrashReturnScreen";

const itemsPerScreen = 2;
const maxScreenCount = 3;
const screenWidth = 1664;

const clickVO = isMobile() ? VO.TB_PROMPT01 : VO.TB_PROMPT02;
const correctAnswerVOs = randomExhaust([VO.TB_COLLECT05, VO.TB_COLLECT06, VO.TB_COLLECT07, VO.TB_COLLECT08]);
const twoItemsOnScreenIdleVO = [clickVO, VO.TB_COLLECT_TO01];
const oneItemLeftIdleVO = randomExhaust([VO.TB_COLLECT_TO02, (it: TrashItem) => [VO.TB_COLLECT_TO03, it.voiceoverC]]);
const oscarReactVO = [VO.TB_COLLECT09, VO.TB_COLLECT10];
HLF.ArrayUtils.shuffle(oscarReactVO);

export class TrashCollectScreen extends AbstractGameState {

    public static readonly NAME = "TrashCollectScreen";

    public readonly main = this.addChild(new Container());
    public readonly bg = array(() => this.main.addChildAt(new Sprite(), 0));
    public readonly bench = this.main.addChild(new Sprite());
    public readonly lamp = this.main.addChild(new Sprite());
    public readonly parkContainer = this.main.addChild(new Container());
    public readonly spawnPoints = array(() => ({x: 0, y: 0}));
    public readonly trashItems: TrashItemCollect[] = ([
        [TrashType.Metal, "recycle-can-beans", VO.TB_TRASH01, VO.TB_TRASHB01, VO.TB_TRASHC01],
        [TrashType.Metal, "recycle-can-tomato", VO.TB_TRASH02, VO.TB_TRASHB02, VO.TB_TRASHC02],
        [TrashType.Metal, "recycle-can-orange-juice", VO.TB_TRASH03, VO.TB_TRASHB03, VO.TB_TRASHC03],
        [TrashType.Plastic, "recycle-bottle-water", VO.TB_TRASH04, VO.TB_TRASHB04, VO.TB_TRASHC04],
        [TrashType.Plastic, "recycle-jug-milk", VO.TB_TRASH05, VO.TB_TRASHB05, VO.TB_TRASHC05],
        [TrashType.Plastic, "recycle-pb", VO.TB_TRASH06, VO.TB_TRASHB06, VO.TB_TRASHC06],
        [TrashType.Paper, "paper-crumpled", VO.TB_TRASH07, VO.TB_TRASHB07, VO.TB_TRASHC07],
        [TrashType.Paper, "paper-newspaper", VO.TB_TRASH08, VO.TB_TRASHB08, VO.TB_TRASHC08],
        [TrashType.Paper, "paper-cup", VO.TB_TRASH09, VO.TB_TRASHB09, VO.TB_TRASHC09],
        [TrashType.Paper, "paper-juicebox", VO.TB_TRASH10, VO.TB_TRASHB10, VO.TB_TRASHC10],
        [TrashType.Paper, "paper-bag", VO.TB_TRASH11, VO.TB_TRASHB11, VO.TB_TRASHC11],
        [TrashType.Paper, "paper-takeout", VO.TB_TRASH12, VO.TB_TRASHB12, VO.TB_TRASHC12],
        [TrashType.Organic, "compost-banana", VO.TB_TRASH13, VO.TB_TRASHB13, VO.TB_TRASHC13],
        [TrashType.Organic, "compost-apple", VO.TB_TRASH14, VO.TB_TRASHB14, VO.TB_TRASHC14],
        [TrashType.Organic, "compost-corn", VO.TB_TRASH15, VO.TB_TRASHB15, VO.TB_TRASHC15],
        [TrashType.Organic, "compost-pretzel", VO.TB_TRASH16, VO.TB_TRASHB16, VO.TB_TRASHC16],
        [TrashType.Organic, "compost-watermelon", VO.TB_TRASH17, VO.TB_TRASHB17, VO.TB_TRASHC17],
        [TrashType.Organic, "compost-eggs", VO.TB_TRASH18, VO.TB_TRASHB18, VO.TB_TRASHC18],
    ] as Array<[TrashType, string, string, string, string]>).map((it) => this.parkContainer.addChild(new TrashItemCollect(it[0], it[1], it[1] !== "compost-banana", it[2], it[3], it[4])));

    public readonly easteregg = [
        // bird
        this.parkContainer.addChild(new TrashBlastEasterEgg({
            frames: 9,
            spriteSheet: ["trashblast-1"],
        })),
        // squirrel
        this.parkContainer.addChild(new TrashBlastEasterEgg({
            frames: 11,
            spriteSheet: ["trashblast-1"],
        })),
    ];

    public readonly oscar = this.parkContainer.addChild(new OscarCollect());

    public readonly hud = this.addChild(new Container());
    public readonly leftButton = this.hud.addChild(new Button());
    public readonly rightButton = this.hud.addChild(new Button());
    public readonly homeButton = this.hud.addChild(new OutlinedButton());
    protected analyticsScreenName: string = "4a Trash Blast_Clean-Up Screen";
    protected analyticsRoundName = "Trash Blast";

    protected screenStartTime: number = 0;
    protected _subscriptions: any[] = [];
    private minY!: number;
    private maxY!: number;

    private screenCount = 0;
    private isMoving: boolean = false;
    private squirrelClickCount: number = 0;
    private pigeonClickCount: number = 0;

    public init(pSize: PIXI.Point, pData?: any) {

        TrashCollectLayout_main.call(this);
        this.main.scale.set(0.5);

        TrashCollectLayout_hud.call(this);
        this.hud.scale.set(0.5);

        this.leftButton.onClick.add(() => {
            HLF.Application.instance.audio.play("buttonclick", 1, false, HLF.AudioCategory.SFX);
            this.goToPreviousScreen();
            this.idleTimeline?.restart(true);
        });

        this.rightButton.onClick.add(() => {
            HLF.Application.instance.audio.play("buttonclick", 1, false, HLF.AudioCategory.SFX);
            this.goToNextScreen();
            this.idleTimeline?.restart(true);
        });

        this.homeButton.onClick.add(() => {
            this.trackEvent("gameMenuClick",
                { label: `MenuButton_Home_ScreenName_${this.analyticsScreenName}` },
            );
            this.gotoNextState("MainMenu");
        });
        this.homeButton.clickSfx = "buttonclick";

        // temporary(?)
        this.leftButton.visible = false;
        this.rightButton.visible = false;

        this.minY = this.spawnPoints.reduce((a, t) => !a || t.y < a.y ? t : a).y;
        this.maxY = this.spawnPoints.reduce((a, t) => !a || t.y > a.y ? t : a).y;
        this.oscar.minY = this.minY;

        // shuffle the whole array
        HLF.ArrayUtils.shuffle(this.trashItems);
        // move last playthrough's items to the end of the array
        this.trashItems.sort((a, b) => TrashGameModel.itemsUsed.indexOf(a.name));

        // get first item of each type
        const metalItem = this.trashItems.filter((it) => it.trashType === TrashType.Metal)[0];
        const plasticItem = this.trashItems.filter((it) => it.trashType === TrashType.Plastic)[0];
        const paperItem = this.trashItems.filter((it) => it.trashType === TrashType.Paper)[0];
        const compostItem = this.trashItems.filter((it) => it.trashType === TrashType.Organic)[0];

        // take the first item from each category and put it at the start of the list
        this.trashItems.unshift(this.trashItems.splice(this.trashItems.findIndex((it) => it === metalItem), 1)[0]);
        this.trashItems.unshift(this.trashItems.splice(this.trashItems.findIndex((it) => it === plasticItem), 1)[0]);
        this.trashItems.unshift(this.trashItems.splice(this.trashItems.findIndex((it) => it === paperItem), 1)[0]);
        this.trashItems.unshift(this.trashItems.splice(this.trashItems.findIndex((it) => it === compostItem), 1)[0]);

        // remove all but six items
        this.trashItems.splice(itemsPerScreen * maxScreenCount);
        // shuffle again
        HLF.ArrayUtils.shuffle(this.trashItems);

        TrashGameModel.itemsUsed = [];

        this.initTrashItems();

        this.easteregg.forEach((egg) => {
            egg.onClick.add(() => {
                if (this.idleStartTime !== undefined) {
                    const idleEndTime = new Date().valueOf();
                    const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                    this.trackEvent("gameTOReEngage", {label: `ScreenName_${this.analyticsScreenName}`, value: idleDuration});
                    this.idleStartTime = undefined;
                }
                this.trackEvent("gameInteraction", {label: `Object_${egg.name.replace(/^\w/, (c) => c.toUpperCase())}_ScreenName_${this.analyticsScreenName}`});

                egg.TurnOn();
                this.oscar.Smile();
                this.idleTimeline?.restart(true);

                if (egg.name.includes("squirrel")) {
                    Application.instance.audio.stop("trash-blast-squirrel-shirp", HLF.AudioCategory.SFX);
                    Application.instance.audio.stop("trash-blast-squirrel-drop-nut", HLF.AudioCategory.SFX);

                    Application.instance.audio.play("trash-blast-squirrel-shirp", 1, false, HLF.AudioCategory.SFX);
                    Application.instance.audio.play("trash-blast-squirrel-drop-nut", 1, false, HLF.AudioCategory.SFX);

                    if (this.squirrelClickCount % 2 === 0) {
                        Application.instance.voiceover.playVO(VO.TB_COLLECT03, {priority: -1, callback: (pDidPlay: boolean) => {
                            if (pDidPlay) {
                                this.squirrelClickCount++;
                                this.trashItems.forEach((it) => {
                                    it.UnHighlight();
                                });
                            }
                        }});
                    }
                    else {
                        this.squirrelClickCount++;
                    }
                }
                else if (egg.name.includes("bird")) {
                    Application.instance.audio.stop("trash-blast-pigeon-peck", HLF.AudioCategory.SFX);
                    Application.instance.audio.play("trash-blast-pigeon-peck", 1, false, HLF.AudioCategory.SFX);
                    if (this.pigeonClickCount % 2 === 0) {
                        Application.instance.voiceover.playVO(VO.TB_COLLECT04, {priority: -1, callback: (pDidPlay: boolean) => {
                            if (pDidPlay) {
                                this.pigeonClickCount++;
                                this.trashItems.forEach((it) => {
                                    it.UnHighlight();
                                });
                            }
                        }});
                    }
                    else {
                        this.pigeonClickCount++;
                    }
                }
            });
        });

        this.updateButtons();

        this.oscar.interactive = true;
        this.oscar.buttonMode = true;
        this.oscar.addListener(HLF.Input.Events.POINTER_TAP, (e: PIXI.InteractionEvent) => {
            this.idleTimeline?.restart(true);
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {label: `ScreenName_${this.analyticsScreenName}`, value: idleDuration});
                this.idleStartTime = undefined;
            }
            if (e.stopped) { return; }
            console.log("oscar clicked", e);

            const voToPlay = oscarReactVO[0];
            // cycle vo only if it actually played
            const onVoDidPlay = (_pTopic: string, _pData: string) => {
                this.oscar.Smile();
                if (_pData === voToPlay) {
                    console.log("vo is playing", voToPlay);
                    if (oscarReactVO[0] === voToPlay) {
                        oscarReactVO.push(oscarReactVO.shift()!);
                    }
                }
                PubSub.unsubscribe(token);
            };

            const token = PubSub.subscribe(HLF.VOICEOVER_STARTED, onVoDidPlay);

            // NB: the callback never fires if the VO is interrupted, which is why we do the pubsub business
            HLF.Application.instance.voiceover.playVO(voToPlay!, HLF.PlayMode.New, (pDidPlay: boolean) => {
                this.idleTimeline?.restart(true);
                if (pDidPlay) {
                    this.trashItems.forEach((it) => {
                        it.UnHighlight();
                    });
                    console.log("vo completed (callback)", voToPlay);
                }
                else {
                    console.log("vo didn't play (callback)", voToPlay);
                }
                PubSub.unsubscribe(token);
            });
        });

        this._subscriptions.push(
            PubSub.subscribe("nozzle clicked", () => {
                this.idleTimeline?.restart(false);
                this.idleTimeline?.seek(this.idleTimeline?.delay(), false);
            }),
        );

        if (pData?.fromMainMenu) {
            this.trackEvent("gameRoundStart",
                {
                    label: `Round Number_${Application.instance.round}_Round Name_${this.analyticsRoundName}_ScreenName_${this.analyticsScreenName}`,
                });
            TrashGameModel.roundStartTime = new Date().valueOf();
        }
        this.trackEvent("gameComponentStart", {
            label: "Simulation_Clean-up Trash",
        });
        this.screenStartTime = new Date().valueOf();

        super.init(pSize); // this calls onResize
    }
    public destroy() {
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
    }
    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        const bgm = HLF.Application.instance.audio.getAudioTrack("trash_blast_loop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("trash_blast_loop", HLF.AudioCategory.BGM);
        if (bgm.isPlaying() === false) {
            bgm.setLooped(true);
            bgm.play();
        }
        Application.instance.voiceover.playVO(VO.TB_COLLECT01, {priority: 2, callback: () => {
            this.idleTimeline.pause();
            this.idleTimeline.call(() => {
                this.trackEvent("gameTimeoutPlay", {label: `ScreenName_${this.analyticsScreenName}`});
                if (this.idleStartTime === undefined) {
                    this.idleStartTime = new Date().valueOf();
                }
                this.trashItems.forEach((it) => {
                    it.UnHighlight();
                });

                let idleVO: string | string[];
                if (this.oscar.collectedItems.length % itemsPerScreen === 0) { // I don't actually have a method to check which items are on screen, but this works
                    idleVO = twoItemsOnScreenIdleVO;
                    this.trashItems.forEach((it) => {
                        if (it.enabled) {
                            it.Highlight();
                        }
                    });
                    PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(idleVO[1], 0, {prompt: clickVO}));
                }
                else {
                    const nextVO = oneItemLeftIdleVO();
                    if (nextVO instanceof Function) {
                        const item = this.trashItems.filter((it) => !it.pickedUp)[0];
                        idleVO = nextVO(item);
                        item.Highlight();
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(idleVO[0], 0, {trashc: idleVO[1]}));
                    }
                    else {
                        idleVO = nextVO;
                        this.trashItems.forEach((it) => {
                            if (it.enabled) {
                                it.Highlight();
                            }
                        });
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(idleVO));
                    }
                }
                Application.instance.voiceover.playVO(idleVO, {
                    mode: PlayMode.New,
                    callback: () => {
                        this.idleTimeline?.restart(true);
                        this.trashItems.forEach((it) => {
                            it.UnHighlight();
                        });
                    },
                    skipCC: true,
                    priority: -1,
                });
            });
            this.idleTimeline.restart(true);
        }});

    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.hud.position.set(-pSize.x / 2, -pSize.y / 2);
        this.homeButton.x = 108 * 2;
        this.homeButton.y = 134.5 * 2;
        // note: HUD scale is 0.5
        this.leftButton.position.set(0, pSize.y);
        this.rightButton.position.set(pSize.x * 2, pSize.y);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.oscar.update(pDeltaTime);
    }

    private initTrashItems() {
        HLF.ArrayUtils.shuffle(this.spawnPoints);

        const firstItem = this.screenCount * itemsPerScreen;
        const lastItem = Math.min(firstItem + itemsPerScreen, this.trashItems.length);

        for (let i = firstItem; i < lastItem; i++) {
            const item = this.trashItems[i];
            const point = this.spawnPoints[i % itemsPerScreen];
            item.x = point.x;
            item.y = point.y;
            if (item.rotatable) {
                item.rotation = HLF.Random.floatBetween(-Math.PI, Math.PI);
            }
            item.scale.set(3);
            item.onDown.add(() => {
                this.trackEvent("gameInteraction", {label: `Object_${item.name.replace(/^\w+\-/, "").replace(/^\w/, (c) => c.toUpperCase())}_ScreenName_${this.analyticsScreenName}`});
                this.idleTimeline?.restart(true);
                if (this.idleStartTime !== undefined) {
                    const idleEndTime = new Date().valueOf();
                    const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                    this.trackEvent("gameTOReEngage", {label: `ScreenName_${this.analyticsScreenName}`, value: idleDuration});
                    this.idleStartTime = undefined;
                }
                this.collectItem(item);
            });
            item.visible = true;
            item.enabled = true;
            TrashGameModel.itemsUsed.push(item.name);
        }

        this.trashItems.filter((it) => it.visible).sort((a, b) => a.y - b.y);

        for (let i = firstItem; i < lastItem; i++) {
            this.trashItems[i].zIndex = i;
        }

        if (lastItem > firstItem) {
            this.spawnPoints.forEach((s) => s.x += this.bg[0].width);
            this.screenCount++;
            if (this.screenCount < maxScreenCount) {
                // recursion
                this.initTrashItems();
            }
            else {
                // remove the rest
                this.trashItems.splice(lastItem);
            }
        }
    }

    private collectItem(item: TrashItemCollect) {

        this.trackEvent("gameQuestionCA", {
            label: `Question Asked_${this.trashItems.filter((it) => !it.pickedUp).map((it) => it.name.replace(/^\w+\-/, "").replace(/^\w/, (c) => c.toUpperCase())).join(", ")}_Question Sequence_${(this.oscar.collectedItems.length + 1)}_Player Answer_${item.name.replace(/^\w+\-/, "").replace(/^\w/, (c) => c.toUpperCase())}`,
        });
        this.trashItems.forEach((it) => {
            it.UnHighlight();
        });

        this.idleTimeline?.pause();

        this.oscar.collectItem(item, () => {
            this.idleTimeline?.restart(true);
            Application.instance.voiceover.playVO(correctAnswerVOs(), HLF.PlayMode.Override, () => {
                this.idleTimeline?.restart(true);
                if (this.oscar.collectedItems.length >= this.trashItems.length) {
                    // All items collected
                    this.onAllItemsCollected();
                }
                else if (this.oscar.collectedItems.length % itemsPerScreen === 0) { // this only works if you can't go to the next screen without collecting all items
                    // All items on this screen collected
                    this.goToNextScreen();
                }
            });
        });
    }

    private onAllItemsCollected() {
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
        this.homeButton.visible = false;
        this.trackEvent("gameComponentComplete", {
            label: "Simulation_Clean-up Trash",
            value: (new Date().valueOf() - this.screenStartTime) / 1000,
        });
        TrashGameModel.collectedItems = this.oscar.collectedItems; // TODO: memory leak?
        TweenLite.delayedCall(1.5, () => {
            if (TrashCollectOutroScreen.visited) {
                this.gotoNextState(TrashReturnScreen.NAME);
            }
            else {
                this.gotoNextState(TrashCollectOutroScreen.NAME);
            }
        });
    }

    private updateButtons() {
        const canGoRight = this.main.position.x > (this.screenCount - 1) * -screenWidth;
        this.rightButton.interactive = canGoRight;
        this.rightButton.alpha = canGoRight ? 1 : 0.5;

        const canGoLeft = this.main.position.x < 0;
        this.leftButton.interactive = canGoLeft;
        this.leftButton.alpha = canGoLeft ? 1 : 0.5;
    }

    private goToNextScreen() {
        this.isMoving = true;
        this.idleTimeline?.pause();
        // disable buttons until after tween
        this.leftButton.interactive = false;
        this.rightButton.interactive = false;

        const duration = 6;

        this.main.moveXTo(this.main.x - screenWidth, duration, Linear.easeNone, () => this.updateButtons());
        // move oscar opposite of bg so he stays in place
        this.oscar.walkTo(this.oscar.x + (screenWidth * 2), screenWidth / duration * 2, Linear.easeNone, () => {
            this.isMoving = false;
            this.idleTimeline?.restart(true);
        }, 2);
        this.oscar.scale.x = Math.abs(this.oscar.scale.x);
    }

    private goToPreviousScreen() {
        this.isMoving = true;
        this.idleTimeline?.pause();
        // disable buttons until after tween
        this.leftButton.interactive = false;
        this.rightButton.interactive = false;
        this.main.moveXTo(this.main.x + screenWidth, 2.5, Linear.easeNone, () => this.updateButtons());
        // move oscar opposite of bg so he stays in place
        this.oscar.walkTo(this.oscar.x - (screenWidth * 2), screenWidth / 2.5 * 2, Linear.easeNone, () => {
            this.isMoving = false;
            this.idleTimeline?.restart(true);
        });
        this.oscar.scale.x = -1 * Math.abs(this.oscar.scale.x);
    }
}
